<template>
  <!-- The table's own width, derived from the headers rather than typed out a
       second time, so the two can never drift. See the min-width rule at the
       foot of this file for why it has to be declared at all. -->
  <div :style="{ '--kf-users-table-w': tableMinWidth + 'px' }">
    <!-- Narrowed to the allowed tab rather than hidden -- see the same note on
         Offer Management. -->
    <ProductSwitch
      :value="product"
      :counts="visibleProductCounts"
      @change="selectProduct"
    />

    <!-- The stages ARE the filter: press one to see only those users, press
         "All users" (or the pressed one again) to clear. The same strip
         Products and Offer Management use, so all three screens name and mark
         a selected filter identically.

         Always drawn. Until the first counts land -- still loading, or failed
         with nothing to keep -- the cells come from the canonical stage list
         (see bucketItems), so the strip shows skeletons while it waits and
         dashes if it gives up, and never collapses to a lone "All users"
         stretched across the page. It used to be hidden until the counts
         arrived, which on a cold cache meant up to 50s with no tabs at all.
         Tabs you can still press beat no tabs at all. -->
    <StatStrip
      :items="bucketItems"
      :active="stage"
      :loading="countsLoading"
      @select="selectStage"
    />

    <FilterBar
      :filters="filterSchema"
      :value="filters"
      :search.sync="search"
      :date-range="dateRange"
      :advanced-count="activeChips.length"
      :result-count="totalCount"
      result-label="users"
      :show-date="!filterLocked('user-management', 'date_range')"
      search-placeholder="Search name, mobile, email or ID"
      @update:dateRange="onDateRange"
      @change="reload"
      @reset="resetFilters"
    >
      <!-- The chips and the applied count both come from the contract module
           (activeFilters over USER_FILTERS), which is the authority on what is
           applied -- so the bar defers to them rather than inferring its own. -->
      <template #chips>
        <v-chip
          v-for="c in activeChips"
          :key="c.key"
          small
          close
          class="kf-fchip"
          @click:close="clearFilter(c.key)"
        >
          <strong>{{ c.label }}:</strong>&nbsp;{{ c.value }}
        </v-chip>
      </template>
    </FilterBar>

    <!-- No @row-click, and no per-row link either. This screen used to end each
         row with an icon opening the legacy Customer details page; that page is
         deliberately not reachable from User Management any more. Everything an
         agent needs to size up a lead is in the row itself, which is why most
         columns carry a second line. -->
    <DataTable
      title="Users"
      :headers="headers"
      :items="items"
      :loading="loading"
      :error="error"
      :page="page"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-length="pageLength"
      empty-title="No users match these filters"
      empty-message="Try a different stage, a wider date range, or clearing a filter."
      empty-icon="users"
      @page-change="onPage"
      @retry="reload"
    >
      <template #actions>
        <DownloadReport :action="downloadReport" />
      </template>
      <!-- Who they are and how to reach them. Age and salary are NOT folded in
           here: they are facts an agent scans down a column for, so they keep
           headings of their own two cells along. The title is because
           kf-truncate clips this line and there is nowhere else to read the
           full value from. -->
      <template #[`item.name`]="{ item }">
        <div class="kf-user">
          <span class="kf-user__avatar">{{ fmt.initials(item.name) }}</span>
          <div class="kf-user__text">
            <div class="kf-user__name">{{ item.name }}</div>
            <div
              class="kf-user__sub kf-truncate"
              :title="`${fmt.mobile(item.mobile)} · ${item.customer_id}`"
            >
              {{ fmt.mobile(item.mobile) }} · {{ item.customer_id }}
            </div>
          </div>
          <!-- Send this one person a notification. The Notifications screen has
               always accepted ?user_id=, but the only link into it was on the
               lead detail page, which nothing in the sidebar reaches any more --
               so in practice individual sends had no way in at all.
               Hidden without the permission: the route guard would bounce them. -->
          <v-btn
            v-if="canNotify"
            icon x-small
            class="kf-user__notify"
            :title="`Notify ${item.name}`"
            :to="{ name: 'karrofin-notifications', query: { user_id: item.user_id || item.id } }"
          >
            <LucideIcon name="bell" :size="15" />
          </v-btn>
        </div>
      </template>

      <!-- Clipped like Employer and City, with the full address on hover. -->
      <template #[`item.email`]="{ item }">
        <div class="kf-truncate" :title="item.email">{{ fmt.orDash(item.email) }}</div>
      </template>

      <!-- Salary and age were one column headed "Profile", which named neither
           of them -- the same split Offer Management makes, down to the muted
           dash when the value is missing. A blank cell reads as a load that
           failed; a dash says the customer never gave us this. -->
      <template #[`item.monthly_income`]="{ item }">
        <div v-if="item.monthly_income" class="kf-stack kf-stack--end">
          <div>
            <span class="kf-salary">{{ fmt.currency(item.monthly_income) }}</span>
            <span class="kf-salary__unit">/mo</span>
          </div>
        </div>
        <span v-else class="kf-muted">-</span>
      </template>

      <!-- Age on top, gender under it: gender qualifies the age rather than
           standing on its own, and it is empty on most rows in the dev data --
           the cell drops to just the age rather than leaving a gap. -->
      <template #[`item.age`]="{ item }">
        <div v-if="item.age" class="kf-stack">
          <div>{{ item.age }}</div>
          <div v-if="item.gender" class="kf-stack__sub">{{ item.gender }}</div>
        </div>
        <span v-else class="kf-muted">-</span>
      </template>

      <template #[`item.employer_name`]="{ item }">
        <div class="kf-stack">
          <div class="kf-truncate" :title="item.employer_name">
            {{ fmt.orDash(item.employer_name) }}
          </div>
          <div class="kf-stack__sub" :title="item.employment_type">
            {{ fmt.orDash(item.employment_type) }}
          </div>
        </div>
      </template>

      <template #[`item.city`]="{ item }">
        <div class="kf-stack">
          <div class="kf-truncate" :title="item.city">{{ fmt.orDash(item.city) }}</div>
          <div class="kf-stack__sub kf-truncate" :title="item.state">
            {{ fmt.orDash(item.state) }}
          </div>
        </div>
      </template>

      <!-- Two different questions in one column: the chip is where the LOAN got
           to, the line under it is where the CUSTOMER got to in onboarding. A
           lead can sit at "Lead" for weeks and the sub-line is the only thing
           that says whether they stalled at KYC or never finished signing up. -->
      <template #[`item.stage`]="{ item }">
        <div class="kf-stack">
          <StatusChip :status="item.stage" small />
          <div class="kf-stack__sub kf-truncate" :title="item.profile_stage">
            {{ fmt.orDash(item.profile_stage) }}
          </div>
        </div>
      </template>

      <!-- The amount rides under the counts rather than in a column of its own:
           the table is at its full width budget, and what a customer was
           offered belongs next to how many offers they have. It is the sum of
           the offers that put them in the SELECTED section, so this figure
           changes as you move between Disbursed and Offer Available -- see
           get_offered_amount server-side. -->
      <template #[`item.offers`]="{ item }">
        <div v-if="item.offer_count" class="kf-stack">
          <!-- The counts open the offers behind them. This is the only
               clickable thing in the row, so it has to look like it: the row
               itself has no click handler and no expansion. -->
          <button type="button" class="kf-offers-link" @click="openOffers(item)">
            {{ item.clicked_count }} / {{ item.offer_count }}
            <span class="kf-offers-link__word">clicked</span>
            <LucideIcon name="arrow-up-right" :size="13" />
          </button>
          <div
            class="kf-stack__sub"
            :title="offeredTitle(item)"
          >
            {{ item.offered_amount ? fmt.currencyShort(item.offered_amount) : "-" }}
          </div>
        </div>
        <span v-else class="kf-muted">None</span>
      </template>

      <!-- Only rendered in the Disbursed section -- see the `headers` computed.
           Sits next to Offers so the offered ceiling and the money that
           actually moved read together; they are usually several times apart. -->
      <template #[`item.disbursed_amount`]="{ item }">
        <span
          v-if="item.disbursed_amount"
          class="kf-amount"
          :title="disbursedTitle(item)"
        >
          {{ fmt.currencyShort(item.disbursed_amount) }}
        </span>
        <span v-else class="kf-muted">-</span>
      </template>

      <template #[`item.lead_source`]="{ item }">
        <div class="kf-stack">
          <div class="kf-truncate" :title="item.lead_source">
            {{ item.lead_source || "Direct" }}
          </div>
          <div class="kf-stack__sub">{{ fmt.orDash(item.platform) }}</div>
        </div>
      </template>

      <!-- No optional chaining: vue-template-compiler 2.6 cannot parse `?.`. -->
      <template #[`item.utm`]="{ item }">
        <a class="kf-utm" @click.stop="ViewUtm(item)">
          {{ (item.utm && item.utm.utm_source) || "View" }}
        </a>
      </template>

      <!-- Relative on top because "3 days ago" is the thing being judged; the
           absolute signup date underneath keeps the old Signed up column's
           information rather than dropping it. -->
      <template #[`item.last_active_date`]="{ item }">
        <div class="kf-stack">
          <div :title="fmt.dateTime(item.last_active_date)">
            {{ fmt.relative(item.last_active_date) }}
          </div>
          <div class="kf-stack__sub" :title="`Signed up ${fmt.date(item.signed_up_date)}`">
            {{ joinedLabel(item) }}
          </div>
        </div>
      </template>
    </DataTable>

    <!-- The legacy screens' dialog, unchanged: no props, reads the six utm_*
         keys off whatever object openDialog is handed. -->
    <ViewUTMDetail ref="UtmDialog" />

    <CustomerOffersDialog v-model="offers_dialog" :customer="offers_target" />
  </div>
</template>

<script>
import FilterBar from "@/components/karrofin/FilterBar.vue";
import ProductSwitch from "@/components/karrofin/ProductSwitch.vue";
import DataTable from "@/components/karrofin/DataTable.vue";
import CustomerOffersDialog from "@/components/karrofin/CustomerOffersDialog.vue";
import StatStrip from "@/components/karrofin/StatStrip.vue";
import DownloadReport from "@/components/karrofin/DownloadReport.vue";
import StatusChip from "@/components/karrofin/StatusChip.vue";
import api from "@/app-js/karrofin/api";
import store, { defaultDateRange, productFromQuery } from "@/app-js/karrofin/store";
import fmt from "@/app-js/karrofin/format";
import {
  LEAD_BUCKET,
  PRODUCT_TYPES,
  USER_STAGES,
  displayLabel,
} from "@/app-js/karrofin/constants";
import {
  USER_FILTERS,
  defaultFilters,
  toParams,
  activeFilters,
  clearOne,
} from "@/app-js/karrofin/filters";
import { PAGE_LENGTH } from "@/app-js/karrofin/config";

/* Every filter this screen offers, in display order -- see the note on
   Offer Management for why order rather than two fixed groups. `key` is the
   lock's parameter name. */
/* The Notifications screen's permission string. It is the route's meta.title
   (see router.js) -- the same value the sidebar filters on and the guard in
   main.js matches, so this is not a second spelling of the rule. */
const NOTIFY_PERMISSION = "KarroFin Notifications";

/* What a tile shows when its number could not be fetched -- an em dash rather
   than a 0, because zero is a fact and this is the absence of one. */
const UNKNOWN = "—";

/**
 * How each filter DRAWS, in the order the panel shows them.
 *
 * What each one MEANS -- its request parameter, what "empty" is, how its chip
 * reads -- stays in USER_FILTERS (app-js/karrofin/filters.js), which is the
 * single writer of filter params. This table is presentation only: the two are
 * joined by `key` in `filterSchema()` below, so a filter cannot be drawn here
 * without a spec entry to send it.
 *
 * `lock` is the name Team -> Filtered view knows a filter by, which is not
 * always the state key: the three band filters are locked as "age", "salary"
 * and "activity" but stored as `age_band`, `salary_band`, `activity_band`.
 *
 * Nothing is pinned, so the compact row stays one line: Search, Date range and
 * the buttons. All seventeen open with "More filters".
 */
const FILTER_RENDER = [
  { key: "city", lock: "city", type: "multiselect", options: "cities" },
  { key: "state", lock: "state", type: "multiselect", options: "states" },
  { key: "platform", lock: "platform", type: "multiselect", options: "platforms" },
  {
    key: "lead_partner",
    lock: "lead_partner",
    type: "multiselect",
    options: "lead_partners",
  },
  {
    key: "age_band",
    lock: "age",
    type: "select",
    options: "age_bands",
    itemText: "name",
    returnObject: true,
    placeholder: "Any age",
  },
  {
    key: "salary_band",
    lock: "salary",
    type: "select",
    options: "salary_bands",
    itemText: "name",
    returnObject: true,
    placeholder: "Any salary",
  },
  /* How recently they were last active, which is the top line of the Activity
     column. Not the date range on the bar above: that one filters on the signup
     date, which is this column's SUB-line. */
  {
    key: "activity_band",
    lock: "activity",
    type: "select",
    options: "activity_bands",
    itemText: "name",
    returnObject: true,
    placeholder: "Any activity",
  },
  /* Attribution. Six of the same control -- the spec key IS the singular of its
     option list, so `optionsKey` derives the list name rather than naming it. */
  { key: "utm_source", lock: "utm_source", type: "multiselect" },
  { key: "utm_medium", lock: "utm_medium", type: "multiselect" },
  { key: "utm_campaign", lock: "utm_campaign", type: "multiselect" },
  { key: "utm_content", lock: "utm_content", type: "multiselect" },
  { key: "utm_term", lock: "utm_term", type: "multiselect" },
  { key: "utm_platform", lock: "utm_platform", type: "multiselect" },
  /* Which KYC document the status below applies to. Leaving it unset matches
     the status on any of the three. */
  {
    key: "kyc_step",
    lock: "kyc_step",
    type: "select",
    options: "kyc_steps",
    placeholder: "Any document",
    dependent: "kyc_status",
  },
  {
    key: "kyc_status",
    lock: "kyc_status",
    type: "select",
    options: "kyc_statuses",
    placeholder: "Any status",
  },
  {
    key: "availed_offer",
    lock: "availed_offer",
    type: "select",
    options: "availed_offer_counts",
    itemText: "name",
    returnObject: true,
    placeholder: "Any number",
  },
];

export default {
  name: "KarroFinUsers",
  components: {
    FilterBar,
    ProductSwitch,
    DataTable,
    CustomerOffersDialog,
    StatStrip,
    DownloadReport,
    StatusChip,
    ViewUTMDetail: () => import("@/components/las/ViewUTMDetail.vue"),
  },
  data() {
    return {
      fmt,
      // Whether to offer the per-row Notify link. Set in created() from the
      // same permission list the sidebar and the route guard read, so the
      // button and the screen it points at agree about who may use it.
      canNotify: false,
      /* Two flags, not one. `loading` is the table; `countsLoading` is the tile
         strip above it. They were a single flag over a single Promise.all, so
         the rows were held back until the slowest count landed -- the screen
         showed skeletons everywhere when only the counts were slow. */
      loading: true,
      countsLoading: true,
      /* Bumped by every load(); see the note there. */
      requestToken: 0,
      error: null,
      items: [],
      counts: [],
      /* The offers popup. `offers_target` is the row it was opened from -- it
         supplies the name, the mobile and the counts the popup echoes back. */
      offers_dialog: false,
      offers_target: null,
      // The whole customer base, from the counts endpoint. Null until it lands.
      bucketTotal: null,
      page: 1,
      totalPages: 1,
      totalCount: 0,
      pageLength: PAGE_LENGTH,
      // Seeded from the URL so a dashboard tile can land here pre-filtered.
      stage: this.$route.query.stage || null,
      product: productFromQuery(this.$route.query.product, PRODUCT_TYPES),
      productCounts: PRODUCT_TYPES.map((label) => ({ label, count: 0 })),
      search: "",
      filters: defaultFilters(USER_FILTERS),
      options: {
        cities: [], states: [], platforms: [], lead_partners: [],
        age_bands: [], salary_bands: [], activity_bands: [],
        // The six attribution lists, from users/get_utm_dropdown/. Declared
        // here so the selects never bind `undefined` on the first paint.
        utm_sources: [], utm_mediums: [], utm_campaigns: [],
        utm_contents: [], utm_terms: [], utm_platforms: [],
        kyc_steps: [], kyc_statuses: [], availed_offer_counts: [],
      },
      // Widths are explicit because table-layout is fixed on this page: a
      // declared width IS the width, and .kf-truncate does the clipping. They
      // total 1209, and nothing has to be kept in step with that by hand --
      // `tableMinWidth` sums this list and the min-width rule at the foot of
      // the file reads it back.
      //
      // 1209 rather than the 1132 this table held for a long time. 1132 was a
      // 1366px laptop minus the 200px legacy drawer and the page gutter, i.e.
      // the widest this table could be without ever scrolling sideways. That
      // stopped being affordable when Profile split into Salary and Age: two
      // headed columns need 180 where the combined one used 103, and the only
      // way to find the other 77 inside 1132 was to take it back off Employer,
      // City, Journey, Source and Activity -- all free text, all of which had
      // just been given that room because they were truncating. So the table is
      // wider than the card and scrolls below roughly 1440px instead, which is
      // what Offer Management already does at 1364.
      //
      // Salary and Age stay COLUMNS. Folding them into the Customer cell as a
      // third line was tried and reverted: it bought 180px of width back, but
      // an agent comparing salaries reads down a column, and a figure stacked
      // under a name is not in a column any more.
      baseHeaders: [
        { text: "Customer", value: "name", width: 192 },
        // Its own column rather than a third line under the name: an agent
        // matching an address reads down it, and the search box finds it.
        { text: "Email", value: "email", width: 186 },
        // Salary and Age were one column headed "Profile", which named neither
        // of them. Same widths Offer Management gives the same two facts.
        { text: "Salary", value: "monthly_income", width: 112, align: "end" },
        // Two digits and a short word under them, so it is the narrowest column
        // in the table. Left-aligned: it is a label, not a figure to compare.
        { text: "Age", value: "age", width: 68 },
        { text: "Employer", value: "employer_name", width: 155 },
        { text: "City", value: "city", width: 127 },
        { text: "Journey", value: "stage", width: 165 },
        { text: "Offers", value: "offers", width: 118 },
        { text: "Source", value: "lead_source", width: 118 },
        // The full attribution behind Source, one click away. Beside it on
        // purpose: Source collapses utm_source/utm_medium into one line and
        // only when there is no partner or referral, so this is where an
        // operator looks when that line does not explain the row.
        { text: "UTM", value: "utm", width: 96 },
        { text: "Activity", value: "last_active_date", width: 154 },
      ],
    };
  },
  computed: {
    /**
     * The columns, with "Loan amount" spliced in for the Disbursed section.
     *
     * That section is the only one where "how much was actually paid out" is a
     * question worth a column, and it is a genuinely different number from the
     * offered figure already under Offers -- the ceiling runs several times the
     * money that moved.
     *
     * Appended rather than paid for. This used to narrow Journey and Employer
     * by 105px between them, because the table was laid out to a width that
     * could not scroll and a tenth column had to come out of the other nine.
     * That budget is gone (see baseHeaders), so the section is simply 105px
     * wider than the rest -- 1314 against 1209 -- and `tableMinWidth` follows
     * it without being told.
     */
    headers() {
      // LEAD_BUCKET.DISBURSED, not a literal: this is the string the server
      // matches on, and it already has a name.
      if (this.stage !== LEAD_BUCKET.DISBURSED) return this.baseHeaders;

      const out = [];
      for (const header of this.baseHeaders) {
        out.push(header);

        // Directly after Offers: the two amounts read together, offered then
        // paid, which is the comparison that makes either of them mean anything.
        if (header.value === "offers") {
          out.push({
            text: "Loan amount",
            value: "disbursed_amount",
            width: 105,
            align: "end",
          });
        }
      }
      return out;
    },
    /**
     * How wide the table actually is, in px.
     *
     * Summed from the columns on screen rather than written down beside them,
     * because a hand-kept copy of this number is a bug waiting to happen: under
     * table-layout: fixed a min-width SMALLER than the declared widths scales
     * every column down to fit, so the declared numbers quietly stop being the
     * real ones. Reads `headers`, not `baseHeaders`, so the Disbursed section's
     * extra column is covered too.
     */
    tableMinWidth() {
      return this.headers.reduce((sum, h) => sum + (h.width || 0), 0);
    },
    /**
     * The filter bar's schema: how each filter draws (FILTER_RENDER) joined to
     * what it means (USER_FILTERS) on `key`, with the ones this member is not
     * allowed dropped.
     *
     * A filter without a spec entry is skipped rather than drawn, so a control
     * can never appear that `toParams` would not send -- which is the failure
     * filters.js exists to prevent.
     *
     * Locked filters are removed here, not hidden in the bar, so the grid
     * simply closes up around them.
     */
    filterSchema() {
      return FILTER_RENDER.filter(
        (r) => !this.filterLocked("user-management", r.lock)
      )
        .map((r) => {
          const spec = USER_FILTERS.find((s) => s.key === r.key);
          if (!spec) return null;
          return {
            key: r.key,
            label: spec.label,
            type: r.type,
            options: this.options[r.options || this.optionsKey(r.key)] || [],
            itemText: r.itemText,
            returnObject: r.returnObject,
            placeholder: r.placeholder,
            // This screen's state is replaced wholesale by the contract module
            // rather than mutated, so the bar hands dependent-field changes
            // back rather than clearing anything itself.
            onChange: r.dependent ? () => this.onDependent(r.dependent) : undefined,
          };
        })
        .filter(Boolean);
    },
    /* Only the product tabs this user is allowed; every tab when unlocked. */
    visibleProductCounts() {
      return this.allowedSegments(
        "user-management", "current_loan_type", this.productCounts, "label");
    },
    dateRange() {
      return store.dateRange;
    },
    /**
     * "All users" plus the four sections, in StatStrip's shape.
     *
     * The total comes from the server (`bucket_total`), not from adding the
     * sections up and not from this.totalCount.
     *
     *  - Adding them up used to be right, back when a customer was in exactly
     *    one section. The sections overlap now -- a customer with a disbursed
     *    offer and an open one is counted in two -- so the sum overstates the
     *    customer base (304 against 203 on dev3).
     *  - totalCount is THIS query's row count, so it would collapse to the
     *    selected section's figure the moment one was pressed.
     *
     * The counts endpoint deliberately ignores `stage`, so its total is the
     * unfiltered customer base while still moving with every other filter.
     *
     * `label` is what is read on screen, `filter` is what the server matches
     * on -- null being the "no stage" that clears the filter.
     */
    bucketItems() {
      const stages = this.counts || [];
      /* Nothing counted yet -- in flight, or the last attempt failed: draw the
         tabs anyway with their values unknown, rather than no tabs at all.
         While loading, StatStrip draws one skeleton per item, so this also
         gives the loading state its real shape. The labels are the
         canonical stage list, so no bucket and no number is invented, and every
         cell still filters the table -- the strip is a filter first and a figure
         second. `isText` is what keeps the dash out of the number formatter,
         which would otherwise turn it into one of its own. */
      const all =
        !stages.length
          ? [
              { label: "All users", value: UNKNOWN, isText: true, filter: null },
              ...USER_STAGES.map((stage) => ({
                label: displayLabel(stage),
                value: UNKNOWN,
                isText: true,
                filter: stage,
              })),
            ]
          : [
              {
                label: "All users",
                // Falls back to the sum only if the server did not send a total
                // -- an overstated tile beats a blank one.
                value:
                  this.bucketTotal !== null
                    ? this.bucketTotal
                    : stages.reduce((sum, c) => sum + (Number(c.count) || 0), 0),
                filter: null,
              },
              ...stages.map((c) => ({
                label: displayLabel(c.label),
                value: c.count,
                filter: c.label,
              })),
            ];
      /* Narrowed to the allowed stage rather than hidden -- the count is
         theirs. "All users" goes too: with a stage pinned it would be a
         second way to ask for everything. */
      const pinned = this.lockedValues("user-management", "stage");
      if (!pinned) return all;
      const kept = all.filter((item) => pinned.indexOf(item.filter) !== -1);
      return kept.length
        ? kept.map((item) => ({
            label: item.label,
            value: item.value,
            isText: item.isText,
          }))
        : all;
    },
    activeChips() {
      return activeFilters(this.filters, USER_FILTERS);
    },
  },
  created() {
    const permissions =
      JSON.parse(sessionStorage.getItem("permission_list")) || [];
    this.canNotify = permissions.indexOf(NOTIFY_PERMISSION) !== -1;

    /* Land a pinned member on the product they are allowed, not on the
       Personal Loan default. */
    const pinned = this.lockedValues("user-management", "current_loan_type");
    if (pinned && pinned.indexOf(this.product) === -1) {
      this.product = pinned[0];
    }
    const pinnedStage = this.lockedValues("user-management", "stage");
    if (pinnedStage) this.stage = pinnedStage[0];
    this.loadOptions();
    this.load();
  },
  methods: {
    displayLabel,
    /** The report is this list, with these filters -- load()'s params, no paging. */
    downloadReport() {
      return api.exportUsers({
        search_query: this.search,
        date_range: this.dateRange,
        product_type: this.product,
        stage: this.stage,
        ...toParams(this.filters, USER_FILTERS),
      });
    },
    load() {
      this.loading = true;
      this.countsLoading = true;
      this.error = null;

      /* Which load this is. Every filter change starts a new one, and a slow
         earlier response must not land on top of a newer one -- three quick
         changes to the filter bar used to leave three requests racing to write
         `items`, and whichever answered last won regardless of which was asked
         last. Each handler checks it is still the newest before writing. */
      const token = ++this.requestToken;
      const current = () => token === this.requestToken;

      const { rows, counts } = api.getUsers({
        page_number: this.page,
        page_length: this.pageLength,
        search_query: this.search,
        date_range: this.dateRange,
        product_type: this.product,
        stage: this.stage,
        // toParams is the only writer of filter params -- see filters.js.
        ...toParams(this.filters, USER_FILTERS),
      });

      /* The table. Settles on its own request alone, so the rows paint while
         the counts are still in flight -- they are two endpoints and the count
         is the slower of the two. */
      rows
        .then((data) => {
          if (!current()) return;
          this.items = data.results;
          this.totalPages = data.total_page_count;
          this.totalCount = data.total_count;
        })
        .catch((err) => {
          if (!current()) return;
          this.error = err;
          this.items = [];
        })
        .finally(() => {
          if (current()) this.loading = false;
        });

      /* The stage tiles and the product tab numbers. A failure here leaves both
         showing what they showed before rather than blanking -- the catch does
         not touch `counts`, the same keep-last-good the product tabs get one
         line above it. getUsers used to swallow the failure and resolve to an
         empty list, which arrived here as a success and emptied the strip. */
      counts
        .then((data) => {
          if (!current()) return;
          this.counts = data.counts || [];
          if (typeof data.total === "number") this.bucketTotal = data.total;
          if (data.product_counts) this.productCounts = data.product_counts;
        })
        // Nothing to do: `counts` keeps its last good value, and with none the
        // strip is already drawn from the stage list with its values unknown.
        .catch(() => {})
        .finally(() => {
          if (current()) this.countsLoading = false;
        });
    },
    /** Row -> UTM popup. `utm` is the group normalise.customerRow builds. */
    ViewUtm(item) {
      this.$refs.UtmDialog.openDialog(item.utm || item);
    },
    /**
     * The option list backing one attribution filter.
     *
     * "utm_source" -> "utm_sources". The dropdown endpoint names its lists in
     * the plural and the filter spec names its keys in the singular, and this is
     * the one place that fact is written down.
     */
    /** The option list a filter reads when FILTER_RENDER does not name one:
     *  always the key plus "s", which is how the options endpoint names them. */
    optionsKey(key) {
      return key + "s";
    },
    /**
     * Dropdown lists, cached in the shared store; a failure leaves them empty.
     *
     * Two sources, merged: cities and states come from the panel's own filter
     * options, the six UTM lists from users/get_utm_dropdown/ -- the same
     * endpoint the legacy customer screen and Notifications already use, so the
     * values offered here are the values those screens offer.
     *
     * Empty on failure rather than falling back to anything invented: a
     * dropdown that offers a campaign nobody was tagged with returns an empty
     * table with nothing to explain it.
     */
    loadOptions() {
      const apply = (data) => {
        this.options = { ...this.options, ...data };
      };

      if (store.filterOptions) apply(store.filterOptions);
      else
        api
          .getFilterOptions()
          .then((data) => {
            store.setFilterOptions(data);
            apply(data);
          })
          .catch(() => {});

      if (store.utmOptions) apply(store.utmOptions);
      else
        api
          .getUtmOptions()
          .then((data) => {
            store.setUtmOptions(data);
            apply(data);
          })
          .catch(() => {});
    },
    reload() {
      this.page = 1;
      this.load();
    },
    onPage(page) {
      this.page = page;
      this.load();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    selectProduct(product) {
      this.product = product;
      this.reload();
    },
    // Clicking the active stage clears it, so the tab strip is its own toggle.
    // StatStrip resolves the press itself and emits null to clear, so this
    // just stores what it was given -- toggling again here would undo it.
    selectStage(stage) {
      this.stage = stage;
      this.reload();
    },
    onDateRange(range) {
      store.setDateRange(range);
    },
    /* Changing the document drops the status that was picked under the old one.
       All three documents carry the same four statuses, so nothing becomes
       impossible -- but a status left in place would keep narrowing the table
       from a control the operator believes they just changed. clearOne handles
       it from the spec's `clears`, so the rule lives in one place. */
    /**
     * A filter that another one hangs off changed -- KYC Document, whose status
     * would otherwise be left over from the previous document and keep
     * narrowing the table under a control the operator thinks they just
     * changed.
     *
     * Takes the DEPENDENT key, not the one that changed: clearOne resets
     * whatever it is given, so passing "kyc_step" here would throw away the
     * document just picked. The spec's `clears` still covers the other
     * direction -- removing the KYC Document chip takes the status with it.
     */
    onDependent(key) {
      this.filters = clearOne(this.filters, key, USER_FILTERS);
      this.reload();
    },
    clearFilter(key) {
      this.filters = clearOne(this.filters, key, USER_FILTERS);
      this.reload();
    },
    resetFilters() {
      // One assignment covers every filter in the spec.
      this.filters = defaultFilters(USER_FILTERS);
      this.stage = null;
      this.search = "";
      store.setDateRange(defaultDateRange());
      this.reload();
    },
    /**
     * "Joined 17 Aug 26" for the sub-line under the last-active date.
     *
     * Not fmt.date: "Joined 17-08-2026" is four characters too wide for this
     * column and was being clipped mid-year. This is a secondary line and the
     * exact date is on the cell's title, so the short form loses nothing.
     */
    joinedLabel(row) {
      if (!row.signed_up_date) return "-";
      const d = new Date(row.signed_up_date);
      if (isNaN(d.getTime())) return "-";
      const short = d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "2-digit",
      });
      return `Joined ${short}`;
    },
    openOffers(row) {
      this.offers_target = row;
      this.offers_dialog = true;
    },
    /** Exact figure behind the abbreviated Loan amount cell. */
    disbursedTitle(row) {
      if (!row.disbursed_amount) return "Nothing disbursed yet";
      return `${fmt.currency(row.disbursed_amount)} disbursed`;
    },
    /**
     * Tooltip for the offered figure: the exact amount, and which offers it
     * covers -- the cell itself is abbreviated and section-scoped, so without
     * this there is no way to tell what was counted.
     */
    offeredTitle(row) {
      if (!row.offered_amount) return "No offer carried a readable amount";
      const scope = this.stage
        ? `their offers in ${displayLabel(this.stage)}`
        : "all their offers";
      return `${fmt.currency(row.offered_amount)} offered across ${scope}`;
    },
  },
};
</script>

<style scoped>
.kf-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
  min-width: 0;
}
.kf-user__avatar {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--kf-brand-soft);
  color: var(--kf-brand);
  font-size: var(--kf-fs-label);
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.kf-user__text {
  min-width: 0;
}
/* Pushed to the right of the cell and kept quiet until the row is hovered --
   it is a secondary action and should not compete with the customer's name. */
.kf-user__notify {
  flex: 0 0 auto;
  margin-left: auto;
  color: var(--kf-brand);
  opacity: 0;
  transition: opacity 0.12s ease-in-out;
}
tr:hover .kf-user__notify,
.kf-user__notify:focus {
  opacity: 1;
}
.kf-user__name {
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kf-user__sub {
  font-size: var(--kf-fs-label);
  color: #000;
}
.kf-salary {
  font-weight: 600;
  color: #000;
}
.kf-salary__unit {
  font-size: var(--kf-fs-label);
  color: #000;
}
/* The disbursed total. Weighted like the salary figure -- it is the number the
   Disbursed section exists to report, so it should not read as a sub-line. */
.kf-amount {
  font-weight: 600;
  color: #000;
  white-space: nowrap;
}
/* The widths in `headers` are a contract here, not a hint. Vuetify's default
   auto layout treats them as minimums and lets a long header label or a long
   city name push a column wider -- which silently shoved the last column off
   the right edge. Fixed layout makes the declared numbers the real ones and
   lets .kf-truncate do the clipping. Scoped to this page: the other KarroFin
   tables share DataTable.vue and are not laid out to declared widths.

   The min-width is the other half of that contract. Fixed layout honours the
   declared widths only while the table box is at least as wide as their sum;
   narrower than that and it scales every column down to fit, so the numbers
   stop meaning anything. It comes from `tableMinWidth`, which sums the columns
   on screen, via a custom property set on the page root -- so there is no
   second copy of the total to keep in step, and the Disbursed section's extra
   column widens the table on its own. Below this width the table scrolls
   sideways inside .kf-table__scroll (DataTable.vue), never the page. */
::v-deep(.kf-table table),
::v-deep .kf-table table {
  table-layout: fixed;
  min-width: var(--kf-users-table-w);
}

/* Every cell that carries two facts uses this pair, rather than a class per
   column -- the City cell had its own before there were eight more like it.
   Colour stays #000 for the sub-line too: the size drop is what separates the
   two lines, and grey-on-white at this size failed to read (see .kf-date note
   that used to live here). */
.kf-stack {
  min-width: 0;
  padding: 2px 0;
}
.kf-stack--end {
  text-align: right;
}
.kf-stack__sub {
  font-size: var(--kf-fs-label);
  color: #000;
  /* Clip rather than wrap. Under fixed layout a sub-line that wraps makes its
     row twice the height of the ones around it, and the column of rows stops
     scanning as a column. The full value stays on the title attribute. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Employer, city and source are free text and will not always fit their
   column. Clipping with an ellipsis keeps the row one height; the full value
   is on the title attribute. */
.kf-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* The one thing in a row you can click, so it has to read as a link before
   anyone hovers it -- this screen deliberately has no row click and no per-row
   icon, so there is nothing else here teaching that cells are interactive. A
   dotted underline says "there is more behind this" without the weight of a
   button in a dense table; it goes solid on hover to confirm the target. */
.kf-offers-link {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  background: none;
  border: none;
  font: inherit;
  font-weight: 600;
  color: var(--kf-brand);
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  cursor: pointer;
  outline: none;
}
.kf-offers-link:hover {
  text-decoration: underline solid;
}
.kf-offers-link:focus-visible {
  text-decoration: underline solid;
  outline: 1px solid var(--kf-brand);
  outline-offset: 2px;
}
/* "clicked" is the unit, not the figure -- lighter so the numbers lead. */
.kf-offers-link__word {
  font-weight: 400;
}
.kf-muted {
  color: #000;
}

/* The UTM cell is the click target for the details popup, so it reads as a
   link rather than as plain text sitting in a column. */
.kf-utm {
  color: #00695c;
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.kf-utm:hover {
  text-decoration: underline;
}
</style>
